import { useMemo, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, KeyRound, Link2, Pencil, Plus, Trash2, X } from "lucide-react";
import {
  FOGBUGZ_LINK_AREA_OVERRIDES_MAX,
  FOGBUGZ_LINK_AREA_PATH_INVALID,
  FOGBUGZ_LINK_AREA_PATH_NOT_FOUND,
  FOGBUGZ_LINK_EXISTS,
  type FogbugzLinkPointingHere,
  type FogbugzLinkTarget,
  type FogbugzProjectLinkInput,
  type FogbugzProjectLinkView,
} from "@ttc-spectree/toolbox-contract/fogbugz";
import { AdoAreaPathPicker, isInOpenAreaPathList } from "@/components/grooming/project/ado-area-path-picker";
import { PROJECT_PRIMARY_BUTTON as primaryButton } from "@/components/grooming/project/project-styles";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useRoutingOptions, type RoutingOptions } from "@/hooks/queries/use-fogbugz";
import {
  useCreateFogbugzLink,
  useDeleteFogbugzLink,
  useFogbugzLinks,
  useGroomingProjects,
  useUpdateFogbugzLink,
} from "@/hooks/queries/use-grooming";
import { useMe } from "@/hooks/queries/use-me";
import { projectsWith, useToolboxAccess } from "@/hooks/queries/use-toolbox-access";
import { ApiError } from "@/lib/api/client";
import { getAdoPatErrorCode } from "@/lib/api/work-items";
import { TOOLBOX_TEXT } from "@/lib/toolbox/type-scale";
import { cn } from "@/lib/utils";

/**
 * The Project page's FogBugz tab (`/grooming/:project/project/fogbugz`,
 * `map-project`; epic PI-SPEC-SW-E308, ER-1664 Package D, feature
 * PI-SPEC-SW-1610; Resolved Decisions 5 and 5b of
 * toolbox-fogbugz-tools-and-product-qa.md): which FogBugz projects feed this
 * ADO project, so the FogBugz tools find the code and the board.
 *
 * - Each link: FogBugz project → this project's default area path (or the
 *   project root), its per-area overrides ("Area <name> → <ADO project or
 *   area path>"), "Code Claude reads" (the mapped repos of every target, or
 *   "Code not set up for <project>"), who changed it last. Edit and Remove
 *   only when the server says the caller may (`editable`: `map-project` on
 *   every ADO project the link targets, or Admin). Remove asks first, naming
 *   the FogBugz project: it is a delete.
 * - "Pointing here": overrides in other projects' links that send FogBugz
 *   areas into this project, read-only.
 * - "Link a FogBugz project" opens the editor: FogBugz project and area
 *   pickers from the person's own FogBugz key (`GET
 *   /fogbugz/interview/routing-options`; areas filtered to the chosen
 *   project here), and ADO area paths picked with {@link AdoAreaPathPicker},
 *   never typed. Override targets are the ADO projects where the person holds
 *   `map-project` (an Admin: every grooming project).
 * - Without a saved FogBugz key the tab says how to save one in Settings and
 *   shows no editor (the lists use the person's own key).
 *
 * Every refusal is a plain sentence: the server's own for 409
 * FOGBUGZ_LINK_EXISTS and 400 FOGBUGZ_LINK_AREA_PATH_NOT_FOUND / _INVALID,
 * and the Project Manager rule for a 403.
 */

/** A select-like control (design `.sel`). */
const SELECT_BOX =
  "h-8 w-full appearance-none rounded-lg border border-[#3F3F46] bg-[#111113] px-2.5 pr-7 text-[13px] text-foreground disabled:opacity-50";

// ---------------------------------------------------------------------------
// Words (pure; exported for tests)
// ---------------------------------------------------------------------------

/** The tab's lead sentence. */
export function fogbugzLinksLead(project: string): string {
  return `Which FogBugz projects feed ${project}. FogBugz tools use this to find the code and the board.`;
}

/** The no-key message. */
export const SAVE_FOGBUGZ_KEY_TEXT =
  "Save your FogBugz API key in Settings to pick FogBugz projects. FogBugz lists use your own key.";

/** The 403 sentence for a link write. */
export function linkForbiddenText(project: string): string {
  return `You need Project Manager on ${project} (and on every ADO project an area points to).`;
}

/** Where a target sends cases: its area path, or the ADO project with "(project root)". */
export function targetLabel(container: string, areaPath: string | null | undefined): string {
  return areaPath ?? `${container} (project root)`;
}

/** "Area Reports → icentral-nt\Web" (an override row). */
export function overrideLabel(areaName: string, container: string, areaPath: string | null | undefined): string {
  return `Area ${areaName} → ${areaPath ?? container}`;
}

/** One target's "Code Claude reads" words: "icentral-nt: repo-a, other/repo-b", or "Code not set up for X". */
export function targetCodeText(target: FogbugzLinkTarget): string {
  if (!target.codeSetUp || target.repos.length === 0) return `Code not set up for ${target.container}`;
  const repos = target.repos.map((ref) =>
    ref.project && ref.project.toLowerCase() !== target.container.toLowerCase() ? `${ref.project}/${ref.repo}` : ref.repo
  );
  return `${target.container}: ${repos.join(", ")}`;
}

/** "Oct 29", with the year when it is not this year. */
function shortDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const sameYear = date.getFullYear() === new Date().getFullYear();
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", ...(sameYear ? {} : { year: "numeric" }) });
}

/** "Updated by Tony Truong on Oct 29" (or "Updated Oct 29" when nobody is named). */
export function updatedLine(link: Pick<FogbugzProjectLinkView, "updatedBy" | "updatedAt">): string {
  const when = shortDate(link.updatedAt);
  return link.updatedBy ? `Updated by ${link.updatedBy.name} on ${when}` : `Updated ${when}`;
}

/** The FogBugz link error code: the API puts it in `error.details.code`, or `error.code`. */
function linkErrorCode(error: unknown): string | undefined {
  if (!(error instanceof ApiError)) return undefined;
  const body = error.data as { error?: { code?: unknown; details?: { code?: unknown } } } | undefined;
  const nested = body?.error?.details?.code;
  if (typeof nested === "string") return nested;
  const code = body?.error?.code;
  return typeof code === "string" ? code : undefined;
}

/** A failed link save or removal in plain words. */
export function linkErrorText(error: unknown, project: string): string {
  const code = linkErrorCode(error);
  const serverMessage =
    error instanceof ApiError && error.message && !error.message.startsWith("API Error:") ? error.message : null;
  if (
    serverMessage &&
    (code === FOGBUGZ_LINK_EXISTS || code === FOGBUGZ_LINK_AREA_PATH_NOT_FOUND || code === FOGBUGZ_LINK_AREA_PATH_INVALID)
  ) {
    return serverMessage;
  }
  if (code === "TOOLBOX_CAPABILITY_REQUIRED" || (error instanceof ApiError && error.response.status === 403)) {
    return linkForbiddenText(project);
  }
  if (getAdoPatErrorCode(error)) {
    return "Connect ADO in Toolbox Setup so SpecTree can check the area paths, then save again.";
  }
  if (serverMessage) return serverMessage;
  return "SpecTree did not answer. Check your connection and try again.";
}

// ---------------------------------------------------------------------------
// The editor
// ---------------------------------------------------------------------------

interface OverrideRow {
  key: number;
  fogbugzAreaId: number | null;
  fogbugzAreaName: string;
  container: string;
  areaPath: string | null;
}

let nextRowKey = 1;

function rowsOf(link: FogbugzProjectLinkView | null): OverrideRow[] {
  return (link?.overrides ?? []).map((override) => ({
    key: nextRowKey++,
    fogbugzAreaId: override.fogbugzAreaId,
    fogbugzAreaName: override.fogbugzAreaName,
    container: override.container,
    areaPath: override.areaPath ?? null,
  }));
}

/** A native select drawn like the design's `.sel`, with its chevron. */
function SelectBox({
  label,
  value,
  onChange,
  disabled,
  children,
  testId,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  children: ReactNode;
  testId?: string;
}) {
  return (
    <span className="relative block min-w-0">
      <select
        aria-label={label}
        className={SELECT_BOX}
        value={value}
        disabled={disabled}
        onChange={(event) => {
          onChange(event.target.value);
        }}
        data-testid={testId}
      >
        {children}
      </select>
      <ChevronDown className={cn("pointer-events-none absolute right-2 top-2 h-4 w-4", TOOLBOX_TEXT.faint)} aria-hidden="true" />
    </span>
  );
}

interface LinkEditorProps {
  project: string;
  /** The link being edited, or null to create one. */
  link: FogbugzProjectLinkView | null;
  routing: RoutingOptions | undefined;
  routingLoading: boolean;
  routingFailed: boolean;
  /** ADO projects an override may point to (this one first). */
  adoProjects: readonly string[];
  /** FogBugz projects already linked by another link of this project (greyed in the picker). */
  takenFogbugzProjectIds: ReadonlySet<number>;
  onClose: () => void;
}

function LinkEditorDialog({
  project,
  link,
  routing,
  routingLoading,
  routingFailed,
  adoProjects,
  takenFogbugzProjectIds,
  onClose,
}: LinkEditorProps) {
  const create = useCreateFogbugzLink(project);
  const update = useUpdateFogbugzLink(project);
  const pending = create.isPending || update.isPending;
  const [fogbugzProjectId, setFogbugzProjectId] = useState<number | null>(link?.fogbugzProjectId ?? null);
  const [areaPath, setAreaPath] = useState<string | null>(link?.areaPath ?? null);
  const [overrides, setOverrides] = useState<OverrideRow[]>(() => rowsOf(link));
  const [error, setError] = useState<string | null>(null);

  const fogbugzProjects = useMemo(() => {
    const listed = [...(routing?.projects ?? [])].sort((a, b) =>
      a.sProject.localeCompare(b.sProject, undefined, { sensitivity: "base" })
    );
    // The link's own project stays pickable even if this key cannot list it.
    if (link && !listed.some((p) => p.ixProject === link.fogbugzProjectId)) {
      listed.unshift({ ixProject: link.fogbugzProjectId, sProject: link.fogbugzProjectName });
    }
    return listed;
  }, [routing, link]);

  const chosenProject = fogbugzProjects.find((p) => p.ixProject === fogbugzProjectId) ?? null;

  const areas = useMemo(() => {
    if (fogbugzProjectId === null) return [];
    const listed = (routing?.areas ?? [])
      .filter((a) => a.ixProject === fogbugzProjectId)
      .map((a) => ({ id: a.ixArea, name: a.sArea }));
    for (const row of overrides) {
      if (row.fogbugzAreaId !== null && !listed.some((a) => a.id === row.fogbugzAreaId)) {
        listed.push({ id: row.fogbugzAreaId, name: row.fogbugzAreaName });
      }
    }
    return listed.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
  }, [routing, fogbugzProjectId, overrides]);

  const setRow = (key: number, patch: Partial<OverrideRow>) => {
    setOverrides((rows) => rows.map((row) => (row.key === key ? { ...row, ...patch } : row)));
  };

  const incomplete = fogbugzProjectId === null || overrides.some((row) => row.fogbugzAreaId === null);

  const save = () => {
    if (incomplete || !chosenProject) return;
    const input: FogbugzProjectLinkInput = {
      fogbugzProjectId: chosenProject.ixProject,
      fogbugzProjectName: chosenProject.sProject,
      areaPath,
      overrides: overrides.map((row) => ({
        fogbugzAreaId: row.fogbugzAreaId ?? 0,
        fogbugzAreaName: row.fogbugzAreaName,
        container: row.container,
        areaPath: row.areaPath,
      })),
    };
    setError(null);
    const done = {
      onSuccess: () => {
        onClose();
      },
      onError: (failure: unknown) => {
        setError(linkErrorText(failure, project));
      },
    };
    if (link) update.mutate({ linkId: link.id, input }, done);
    else create.mutate(input, done);
  };

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next && !pending) onClose();
      }}
    >
      <DialogContent
        className="max-h-[calc(100vh-80px)] max-w-[720px] gap-0 overflow-y-auto border-[#27272A] bg-[#111113] p-0 shadow-[0_24px_60px_rgba(0,0,0,0.6)]"
        overlayClassName="bg-black/60"
        data-testid="fogbugz-link-editor"
        onEscapeKeyDown={(event) => {
          // Escape in an open area-path list closes the list, not the editor.
          if (isInOpenAreaPathList(event.target)) event.preventDefault();
        }}
      >
        <DialogHeader className="space-y-1 px-5 pb-3 pr-10 pt-[18px] text-left">
          <DialogTitle className="font-display text-base font-semibold leading-snug">
            {link ? `Edit the link for ${link.fogbugzProjectName}` : "Link a FogBugz project"}
          </DialogTitle>
          <DialogDescription className={cn(TOOLBOX_TEXT.content, "text-muted-foreground")}>
            Cases from this FogBugz project go to {project} unless an area below sends them elsewhere.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4 border-t border-[#1F1F23] px-5 py-4">
          {routingFailed ? (
            <p className={cn(TOOLBOX_TEXT.content, "text-destructive")} role="alert" data-testid="routing-error">
              Your FogBugz lists could not be read with your key. Check the key in Settings, then try again.
            </p>
          ) : null}

          <label className={cn(TOOLBOX_TEXT.meta, "flex flex-col gap-1 text-muted-foreground")}>
            FogBugz project
            <SelectBox
              label="FogBugz project"
              value={fogbugzProjectId === null ? "" : String(fogbugzProjectId)}
              disabled={pending || routingLoading}
              onChange={(value) => {
                const next = value ? Number(value) : null;
                if (next !== fogbugzProjectId) setOverrides([]);
                setFogbugzProjectId(next);
              }}
              testId="fogbugz-project-select"
            >
              <option value="">{routingLoading ? "Loading your FogBugz projects…" : "Pick a FogBugz project"}</option>
              {fogbugzProjects.map((p) => {
                const taken = takenFogbugzProjectIds.has(p.ixProject) && p.ixProject !== link?.fogbugzProjectId;
                return (
                  <option key={p.ixProject} value={String(p.ixProject)} disabled={taken}>
                    {taken ? `${p.sProject} (already linked)` : p.sProject}
                  </option>
                );
              })}
            </SelectBox>
          </label>

          <div className={cn(TOOLBOX_TEXT.meta, "flex flex-col gap-1 text-muted-foreground")}>
            <span>Default area path in {project}</span>
            <AdoAreaPathPicker
              project={project}
              value={areaPath}
              onChange={setAreaPath}
              label="Default area path"
              disabled={pending}
              testId="default-area-path"
            />
          </div>

          <div className="flex flex-col gap-2" data-testid="override-rows">
            <span className={TOOLBOX_TEXT.eyebrow}>Areas that go somewhere else</span>
            {overrides.length === 0 ? (
              <p className={cn(TOOLBOX_TEXT.meta, "text-muted-foreground")}>
                None: every area of this FogBugz project goes to the default above.
              </p>
            ) : null}
            {overrides.map((row, index) => {
              const usedElsewhere = new Set(
                overrides.filter((other) => other.key !== row.key).map((other) => other.fogbugzAreaId)
              );
              const n = String(index + 1);
              return (
                <div
                  key={row.key}
                  className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1.4fr)_auto] items-center gap-2"
                  data-testid="override-row"
                >
                  <SelectBox
                    label={`FogBugz area for override ${n}`}
                    value={row.fogbugzAreaId === null ? "" : String(row.fogbugzAreaId)}
                    disabled={pending}
                    onChange={(value) => {
                      const area = areas.find((a) => String(a.id) === value);
                      setRow(row.key, {
                        fogbugzAreaId: area?.id ?? null,
                        fogbugzAreaName: area?.name ?? "",
                      });
                    }}
                  >
                    <option value="">Pick a FogBugz area</option>
                    {areas.map((a) => (
                      <option key={a.id} value={String(a.id)} disabled={usedElsewhere.has(a.id)}>
                        {a.name}
                      </option>
                    ))}
                  </SelectBox>
                  <SelectBox
                    label={`ADO project for override ${n}`}
                    value={row.container}
                    disabled={pending}
                    onChange={(value) => {
                      if (value !== row.container) setRow(row.key, { container: value, areaPath: null });
                    }}
                  >
                    {(adoProjects.some((p) => p.toLowerCase() === row.container.toLowerCase())
                      ? adoProjects
                      : [...adoProjects, row.container]
                    ).map((name) => (
                      <option key={name} value={name}>
                        {name}
                      </option>
                    ))}
                  </SelectBox>
                  <AdoAreaPathPicker
                    project={row.container}
                    value={row.areaPath}
                    onChange={(value) => {
                      setRow(row.key, { areaPath: value });
                    }}
                    label={`Area path for override ${n}`}
                    disabled={pending}
                    testId={`override-area-path-${n}`}
                  />
                  <button
                    type="button"
                    className={cn("flex h-8 w-8 items-center justify-center rounded-lg hover:bg-[#141417]", TOOLBOX_TEXT.faint)}
                    aria-label={`Remove override ${n}`}
                    disabled={pending}
                    onClick={() => {
                      setOverrides((rows) => rows.filter((other) => other.key !== row.key));
                    }}
                  >
                    <X className="h-4 w-4" aria-hidden="true" />
                  </button>
                </div>
              );
            })}
            <div>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                disabled={
                  pending || fogbugzProjectId === null || overrides.length >= FOGBUGZ_LINK_AREA_OVERRIDES_MAX
                }
                onClick={() => {
                  setOverrides((rows) => [
                    ...rows,
                    { key: nextRowKey++, fogbugzAreaId: null, fogbugzAreaName: "", container: project, areaPath: null },
                  ]);
                }}
              >
                <Plus aria-hidden="true" />
                Add an area override
              </Button>
            </div>
          </div>

          {error ? (
            <p className={cn(TOOLBOX_TEXT.content, "text-destructive")} role="alert" data-testid="link-save-error">
              {error}
            </p>
          ) : null}
        </div>

        <div className="flex justify-end gap-2 border-t border-[#1F1F23] px-5 py-3">
          <Button type="button" size="sm" variant="ghost" disabled={pending} onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" size="sm" className={primaryButton} disabled={pending || incomplete} onClick={save}>
            {pending ? "Saving…" : link ? "Save link" : "Link project"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Remove
// ---------------------------------------------------------------------------

function RemoveLinkDialog({
  project,
  link,
  onClose,
}: {
  project: string;
  link: FogbugzProjectLinkView;
  onClose: () => void;
}) {
  const remove = useDeleteFogbugzLink(project);
  const [error, setError] = useState<string | null>(null);
  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next && !remove.isPending) onClose();
      }}
    >
      <DialogContent
        className="max-w-[480px] gap-0 border-[#27272A] bg-[#111113] p-0"
        overlayClassName="bg-black/60"
        data-testid="remove-link-dialog"
      >
        <DialogHeader className="space-y-1 px-5 pb-3 pr-10 pt-[18px] text-left">
          <DialogTitle className="font-display text-base font-semibold leading-snug">
            Remove the link for {link.fogbugzProjectName}?
          </DialogTitle>
          <DialogDescription className={cn(TOOLBOX_TEXT.content, "text-muted-foreground")}>
            FogBugz tools will no longer find the code or the board for cases in {link.fogbugzProjectName}. Its area
            overrides go too.
          </DialogDescription>
        </DialogHeader>
        {error ? (
          <p className={cn(TOOLBOX_TEXT.content, "px-5 pb-2 text-destructive")} role="alert" data-testid="link-remove-error">
            {error}
          </p>
        ) : null}
        <div className="flex justify-end gap-2 border-t border-[#1F1F23] px-5 py-3">
          <Button type="button" size="sm" variant="ghost" disabled={remove.isPending} onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            variant="destructive"
            disabled={remove.isPending}
            onClick={() => {
              setError(null);
              remove.mutate(link.id, {
                onSuccess: onClose,
                onError: (failure) => {
                  setError(linkErrorText(failure, project));
                },
              });
            }}
          >
            {remove.isPending ? "Removing…" : "Remove link"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// The list
// ---------------------------------------------------------------------------

function LinkCard({
  link,
  canEdit,
  onEdit,
  onRemove,
}: {
  link: FogbugzProjectLinkView;
  canEdit: boolean;
  onEdit: () => void;
  onRemove: () => void;
}) {
  return (
    <li className="glass-card flex flex-col gap-2 rounded-[10px] px-[18px] py-3.5" data-testid="fogbugz-link">
      <div className="flex flex-wrap items-start gap-2">
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className={cn(TOOLBOX_TEXT.content, "font-semibold")} data-testid="link-heading">
            {link.fogbugzProjectName} → {targetLabel(link.container, link.areaPath)}
          </span>
          <span className={cn(TOOLBOX_TEXT.meta, TOOLBOX_TEXT.faint)} data-testid="link-updated">
            {updatedLine(link)}
          </span>
        </div>
        {link.editable ? (
          <div className="flex gap-1">
            {canEdit ? (
              <Button type="button" size="sm" variant="ghost" onClick={onEdit} aria-label={`Edit ${link.fogbugzProjectName}`}>
                <Pencil aria-hidden="true" />
                Edit
              </Button>
            ) : null}
            <Button type="button" size="sm" variant="ghost" onClick={onRemove} aria-label={`Remove ${link.fogbugzProjectName}`}>
              <Trash2 aria-hidden="true" />
              Remove
            </Button>
          </div>
        ) : null}
      </div>
      {link.overrides.length > 0 ? (
        <ul className="flex flex-col gap-0.5" data-testid="link-overrides">
          {link.overrides.map((override) => (
            <li key={override.fogbugzAreaId} className={cn(TOOLBOX_TEXT.content, "text-muted-foreground")}>
              {overrideLabel(override.fogbugzAreaName, override.container, override.areaPath)}
            </li>
          ))}
        </ul>
      ) : null}
      <div className={cn(TOOLBOX_TEXT.meta, "flex flex-col gap-0.5 text-muted-foreground")} data-testid="link-code">
        <span className={TOOLBOX_TEXT.eyebrow}>Code Claude reads</span>
        {link.targets.map((target) => (
          <span key={target.container} data-testid="link-target">
            {targetCodeText(target)}
          </span>
        ))}
      </div>
    </li>
  );
}

function PointingHereList({ entries }: { entries: readonly FogbugzLinkPointingHere[] }) {
  if (entries.length === 0) return null;
  return (
    <section className="flex flex-col gap-2" data-testid="pointing-here">
      <h2 className={TOOLBOX_TEXT.eyebrow}>Pointing here</h2>
      <ul className="glass-card flex flex-col gap-1 rounded-[10px] px-[18px] py-3">
        {entries.map((entry) => (
          <li
            key={`${entry.linkId}-${String(entry.fogbugzAreaId)}`}
            className={cn(TOOLBOX_TEXT.content, "text-muted-foreground")}
            data-testid="pointing-here-row"
          >
            {entry.fogbugzProjectName}: area {entry.fogbugzAreaName} → {entry.areaPath ?? "project root"}
            <span className={cn(TOOLBOX_TEXT.meta, TOOLBOX_TEXT.faint)}> · set on {entry.fromContainer}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

type EditorState = { link: FogbugzProjectLinkView | null } | null;

export interface FogbugzLinksTabProps {
  project: string;
}

export function FogbugzLinksTab({ project }: FogbugzLinksTabProps) {
  const me = useMe();
  const hasFogbugzKey = me.data?.hasFogbugzKey ?? false;
  const linksQuery = useFogbugzLinks(project);
  const routing = useRoutingOptions({ enabled: hasFogbugzKey });
  const { data: access } = useToolboxAccess();
  const isAdmin = access?.isAdmin ?? false;
  const groomingProjects = useGroomingProjects({ enabled: isAdmin });
  const [editor, setEditor] = useState<EditorState>(null);
  const [removing, setRemoving] = useState<FogbugzProjectLinkView | null>(null);

  const adoProjects = useMemo(() => {
    const listed = isAdmin
      ? (groomingProjects.data ?? []).map((p) => p.project)
      : projectsWith(access, "map-project");
    const seen = new Set([project.toLowerCase()]);
    const others: string[] = [];
    for (const name of listed) {
      if (!seen.has(name.toLowerCase())) {
        seen.add(name.toLowerCase());
        others.push(name);
      }
    }
    others.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
    return [project, ...others];
  }, [isAdmin, groomingProjects.data, access, project]);

  const links = linksQuery.data?.links ?? [];
  const takenFogbugzProjectIds = useMemo(() => new Set(links.map((l) => l.fogbugzProjectId)), [links]);

  return (
    <div className="flex flex-col gap-3.5" data-testid="project-fogbugz">
      <div className="flex flex-wrap items-center gap-2.5">
        <p className={cn(TOOLBOX_TEXT.content, "min-w-0 flex-1 leading-relaxed text-muted-foreground")} data-testid="fogbugz-links-lead">
          {fogbugzLinksLead(project)}
        </p>
        {hasFogbugzKey ? (
          <Button
            type="button"
            size="sm"
            className={primaryButton}
            onClick={() => {
              setEditor({ link: null });
            }}
          >
            <Link2 aria-hidden="true" />
            Link a FogBugz project
          </Button>
        ) : null}
      </div>

      {!me.isLoading && !hasFogbugzKey ? (
        <div className="glass-card flex items-start gap-3 rounded-[10px] px-[18px] py-3.5" data-testid="fogbugz-links-needs-key">
          <KeyRound className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          <p className={cn(TOOLBOX_TEXT.content, "flex-1 leading-relaxed text-muted-foreground")}>{SAVE_FOGBUGZ_KEY_TEXT}</p>
          <Button asChild variant="outline" size="sm">
            <Link to="/settings">Settings</Link>
          </Button>
        </div>
      ) : null}

      {linksQuery.isLoading ? (
        <p className={cn(TOOLBOX_TEXT.content, "text-muted-foreground")} role="status">
          Loading…
        </p>
      ) : linksQuery.isError ? (
        <p className={cn(TOOLBOX_TEXT.content, "text-destructive")} role="alert" data-testid="fogbugz-links-error">
          {linksQuery.error instanceof ApiError && linksQuery.error.response.status === 403
            ? `You need Project Manager on ${project} to see its FogBugz links.`
            : "The FogBugz links could not be loaded. Try again in a moment."}
        </p>
      ) : (
        <>
          {links.length === 0 ? (
            <p className={cn(TOOLBOX_TEXT.content, "text-muted-foreground")} data-testid="fogbugz-links-empty">
              No FogBugz project is linked to {project} yet.
            </p>
          ) : (
            <ul className="flex flex-col gap-2.5" data-testid="fogbugz-links">
              {links.map((link) => (
                <LinkCard
                  key={link.id}
                  link={link}
                  canEdit={hasFogbugzKey}
                  onEdit={() => {
                    setEditor({ link });
                  }}
                  onRemove={() => {
                    setRemoving(link);
                  }}
                />
              ))}
            </ul>
          )}
          <PointingHereList entries={linksQuery.data?.pointingHere ?? []} />
        </>
      )}

      {editor && hasFogbugzKey ? (
        <LinkEditorDialog
          project={project}
          link={editor.link}
          routing={routing.data?.data}
          routingLoading={routing.isLoading}
          routingFailed={routing.isError}
          adoProjects={adoProjects}
          takenFogbugzProjectIds={takenFogbugzProjectIds}
          onClose={() => {
            setEditor(null);
          }}
        />
      ) : null}
      {removing ? (
        <RemoveLinkDialog
          project={project}
          link={removing}
          onClose={() => {
            setRemoving(null);
          }}
        />
      ) : null}
    </div>
  );
}
